import { Phone, AlertTriangle, Clock, ShieldCheck, Camera } from "lucide-react";
import { SITE } from "../lib/site";
import { useT } from "../lib/i18n";

export default function Urgence() {
  const { t } = useT();
  const items = t("urgence.treatedItems");
  const waHref = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(t("urgence.waMessage"))}`;

  return (
    <section
      id="urgence"
      data-testid="urgence-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FFE0E0] opacity-50 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#FFEEEE] opacity-60 blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10">
          <div
            data-testid="urgence-led-badge"
            className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-[#D32F2F] text-white shadow-lg shadow-[#D32F2F]/30 mb-6"
          >
            <span className="relative flex w-3 h-3">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white shadow-[0_0_12px_rgba(255,255,255,0.9)]" />
            </span>
            <span className="text-sm font-bold uppercase tracking-[0.3em]">
              {t("urgence.badge")}
            </span>
          </div>

          <h2 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A2E1C] leading-[1.05]">
            {t("urgence.title1")}{" "}
            <span className="font-medium text-[#D32F2F]">
              {t("urgence.title2")}
            </span>
          </h2>
        </div>

        <div className="relative bg-gradient-to-br from-[#D32F2F] via-[#C62828] to-[#B71C1C] rounded-[2.5rem] p-8 sm:p-12 shadow-2xl shadow-[#D32F2F]/30 overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white blur-3xl" />
            <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-white blur-3xl" />
          </div>
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-[repeating-linear-gradient(45deg,#FFD600_0,#FFD600_12px,#1A1A1A_12px,#1A1A1A_24px)] opacity-90" />
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-[repeating-linear-gradient(45deg,#FFD600_0,#FFD600_12px,#1A1A1A_12px,#1A1A1A_24px)] opacity-90" />

          <div className="relative grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-3 text-white text-center lg:text-left">
              <div className="text-xs uppercase tracking-[0.3em] text-white/80 font-semibold mb-3 flex items-center justify-center lg:justify-start gap-2">
                <Phone className="w-4 h-4" />
                {t("urgence.callEyebrow")}
              </div>
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="urgence-call-cta"
                className="block group"
              >
                <div className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none group-hover:scale-[1.02] transition-transform origin-left">
                  {SITE.phone}
                </div>
                <div className="mt-3 inline-flex items-center gap-2 text-white/90 group-hover:text-white text-sm font-medium underline underline-offset-4">
                  {t("urgence.callTap")}
                </div>
              </a>
            </div>

            <div className="lg:col-span-2">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="urgence-whatsapp-cta"
                className="group flex items-center gap-4 w-full bg-white hover:bg-[#F1F8F2] text-[#1A2E1C] rounded-2xl p-5 transition-all hover:-translate-y-0.5 shadow-xl"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#25D366] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
                    <path d="M17.6 6.31A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-6.88 11.93L4 20l4.2-1.1a7.94 7.94 0 0 0 3.85 1h.01a7.93 7.93 0 0 0 7.94-7.93 7.88 7.88 0 0 0-2.4-5.66Zm-5.55 12.2h-.01a6.6 6.6 0 0 1-3.36-.92l-.24-.14-2.5.66.67-2.43-.16-.25a6.6 6.6 0 0 1 5.6-10.1 6.55 6.55 0 0 1 4.66 1.93 6.55 6.55 0 0 1 1.93 4.66 6.6 6.6 0 0 1-6.59 6.59Zm3.62-4.94c-.2-.1-1.18-.58-1.36-.65-.18-.07-.31-.1-.45.1-.13.2-.51.65-.62.78-.12.13-.23.15-.42.05a5.4 5.4 0 0 1-1.6-.99 6 6 0 0 1-1.1-1.38c-.12-.2 0-.3.09-.4.09-.1.2-.23.3-.34.1-.11.13-.2.2-.33.06-.13.03-.25-.02-.35-.05-.1-.45-1.09-.62-1.5-.16-.39-.33-.34-.45-.34l-.39-.01a.75.75 0 0 0-.54.25c-.19.2-.71.7-.71 1.7s.73 1.97.83 2.1c.1.14 1.43 2.18 3.46 3.06.49.21.87.34 1.17.43.49.16.93.13 1.28.08.39-.06 1.18-.48 1.35-.95.16-.46.16-.86.12-.94-.05-.09-.18-.14-.38-.24Z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-['Outfit'] font-bold text-lg">
                    {t("urgence.waTitle")}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[#5C705E] mt-0.5">
                    <Camera className="w-3 h-3" />
                    {t("urgence.waSub")}
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <span className="text-xs uppercase tracking-widest text-[#5C705E] font-semibold mr-2">
            {t("urgence.treatedLabel")}
          </span>
          {items.map((n, i) => (
            <span
              key={n}
              data-testid={`urgence-nuisible-tag-${i}`}
              className="px-4 py-2 rounded-full bg-white border border-[#E5EBE5] text-sm font-medium text-[#1A2E1C] shadow-sm"
            >
              {n}
            </span>
          ))}
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-4">
          <div
            data-testid="urgence-trust-rapide"
            className="flex items-center gap-3 p-5 rounded-2xl bg-white border border-[#E5EBE5] shadow-sm"
          >
            <div className="w-11 h-11 rounded-xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-[#1B5E20]" />
            </div>
            <div>
              <div className="font-['Outfit'] font-semibold text-[#1A2E1C] text-sm">
                {t("urgence.trustRapide")}
              </div>
              <div className="text-xs text-[#5C705E]">{t("urgence.trustRapideSub")}</div>
            </div>
          </div>
          <div
            data-testid="urgence-trust-pro"
            className="flex items-center gap-3 p-5 rounded-2xl bg-white border border-[#E5EBE5] shadow-sm"
          >
            <div className="w-11 h-11 rounded-xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#1B5E20]" />
            </div>
            <div>
              <div className="font-['Outfit'] font-semibold text-[#1A2E1C] text-sm">
                {t("urgence.trustPro")}
              </div>
              <div className="text-xs text-[#5C705E]">{t("urgence.trustProSub")}</div>
            </div>
          </div>
          <div
            data-testid="urgence-seasonal"
            className="flex items-center gap-3 p-5 rounded-2xl bg-[#FFF8E1] border border-[#FFD54F]/60 shadow-sm"
          >
            <div className="w-11 h-11 rounded-xl bg-[#FFD54F] flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-[#7B5300]" />
            </div>
            <div>
              <div className="font-['Outfit'] font-semibold text-[#7B5300] text-sm">
                {t("urgence.seasonal")}
              </div>
              <div className="text-xs text-[#9A6800]">
                {t("urgence.seasonalSub")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
